import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { Input } from "@ember/component";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { cancel } from "@ember/runloop";
import { service } from "@ember/service";
import EmojiPicker from "discourse/components/emoji-picker";
import DTooltip from "discourse/float-kit/components/d-tooltip";
import { ajax } from "discourse/lib/ajax";
import { extractError } from "discourse/lib/ajax-error";
import discourseDebounce from "discourse/lib/debounce";
import { not } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import DModal from "discourse/ui-kit/d-modal";
import DModalCancel from "discourse/ui-kit/d-modal-cancel";
import { i18n } from "discourse-i18n";
import slugifyChannel from "discourse/plugins/chat/discourse/lib/slugify-channel";

const SLUG_MAX_LENGTH = 100;

export default class ChatModalEditChannelName extends Component {
  @service chatApi;
  @service router;
  @service siteSettings;

  @tracked editedName = this.channel.title;
  @tracked editedSlug = this.channel.slug;
  @tracked editedEmoji = this.channel.emoji;
  @tracked
  autoGeneratedSlug = this.channel.slug ?? slugifyChannel(this.channel);
  @tracked flash;

  #generateSlugHandler = null;

  get channel() {
    return this.args.model;
  }

  get isSaveDisabled() {
    return (
      (this.channel.title === this.editedName &&
        this.channel.slug === this.editedSlug &&
        this.channel.emoji === this.editedEmoji) ||
      this.editedName?.length > this.siteSettings.max_topic_title_length ||
      this.editedSlug?.length > SLUG_MAX_LENGTH
    );
  }

  @action
  async onSave() {
    try {
      const result = await this.chatApi.updateChannel(this.channel.id, {
        name: this.editedName,
        slug: this.editedSlug || this.autoGeneratedSlug || this.channel.slug,
        emoji: this.editedEmoji,
      });

      this.channel.title = result.channel.title;
      this.channel.slug = result.channel.slug;
      this.channel.emoji = result.channel.emoji;
      await this.args.closeModal();
      this.router.replaceWith("chat.channel", ...this.channel.routeModels);
    } catch (error) {
      this.flash = extractError(error);
    }
  }

  @action
  onChangeChatChannelName(event) {
    this.flash = null;
    this.#debouncedGenerateSlug(event?.target?.value);
  }

  @action
  onChangeChatChannelSlug() {
    this.flash = null;
    this.#debouncedGenerateSlug(this.editedName);
  }

  @action
  emojiSelected(emoji) {
    this.editedEmoji = emoji;
  }

  @action
  resetEmoji() {
    this.editedEmoji = null;
  }

  #debouncedGenerateSlug(name) {
    cancel(this.#generateSlugHandler);
    this.autoGeneratedSlug = "";

    if (!name) {
      return;
    }

    this.#generateSlugHandler = discourseDebounce(
      this,
      this.#generateSlug,
      name,
      300
    );
  }

  async #generateSlug(name) {
    try {
      await ajax("/slugs.json", { type: "POST", data: { name } }).then(
        (response) => {
          this.autoGeneratedSlug = response.slug;
        }
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }

  <template>
    <DModal
      class="chat-modal-edit-channel-name"
      @closeModal={{@closeModal}}
      @flash={{this.flash}}
      @inline={{@inline}}
      @title={{i18n "chat.channel_edit_name_slug_modal.title"}}
    >
      <:body>
        <div class="edit-channel-control">
          <label class="edit-channel-label" for="channel-name">
            {{i18n "chat.channel_edit_name_slug_modal.name"}}
          </label>
          <Input
            class="chat-channel-edit-name-slug-modal__name-input"
            name="channel-name"
            placeholder={{i18n
              "chat.channel_edit_name_slug_modal.input_placeholder"
            }}
            @type="text"
            @value={{this.editedName}}
            {{on "input" this.onChangeChatChannelName}}
          />
        </div>

        <div class="edit-channel-control">
          <label class="edit-channel-label" for="channel-slug">
            {{i18n "chat.channel_edit_name_slug_modal.slug"}}&nbsp;
            <DTooltip
              @content={{i18n
                "chat.channel_edit_name_slug_modal.slug_description"
              }}
              @icon="circle-info"
            />
          </label>
          <Input
            class="chat-channel-edit-name-slug-modal__slug-input"
            name="channel-slug"
            placeholder={{this.autoGeneratedSlug}}
            @type="text"
            @value={{this.editedSlug}}
            {{on "input" this.onChangeChatChannelSlug}}
          />
        </div>
        <div class="edit-channel-control">
          <label class="edit-channel-label" for="channel-emoji">
            {{i18n "chat.channel_edit_name_slug_modal.emoji"}}
          </label>

          <EmojiPicker
            @btnClass="btn-default btn-emoji"
            @context="channel-emoji"
            @didSelectEmoji={{this.emojiSelected}}
            @emoji={{this.editedEmoji}}
            @inline={{true}}
            @modalForMobile={{false}}
            @showCaret={{true}}
          />

          <DButton
            class="btn-flat edit-channel-clear-emoji"
            @action={{this.resetEmoji}}
            @disabled={{not this.editedEmoji}}
            @label="chat.channel_edit_name_slug_modal.reset_emoji"
          />

        </div>
      </:body>
      <:footer>
        <DButton
          class="btn-primary create"
          @action={{this.onSave}}
          @disabled={{this.isSaveDisabled}}
          @label="save"
        />
        <DModalCancel @close={{@closeModal}} />
      </:footer>
    </DModal>
  </template>
}
